import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Loader2 } from 'lucide-react';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  onContinue: () => void;
}

/**
 * Friendly session timeout modal similar to Beacon's "Are you still there?" dialog
 * Attempts silent re-authentication first, so user stays on the same page
 */
export function SessionTimeoutModal({ isOpen, onContinue }: SessionTimeoutModalProps) {
  const { instance, accounts } = useMsal();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  if (!isOpen) return null;

  const handleContinue = async () => {
    setIsAuthenticating(true);

    try {
      // First, try silent token acquisition - this usually works since
      // the user's Microsoft session is still valid
      if (accounts.length > 0) {
        const response = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email', 'User.Read'],
          account: accounts[0],
        });

        if (response) {
          console.log('Session restored silently');
          onContinue();
          return;
        }
      }

      // If silent auth failed, try popup (less disruptive than redirect)
      try {
        await instance.loginPopup({
          scopes: ['openid', 'profile', 'email', 'User.Read'],
        });
        console.log('Session restored via popup');
        onContinue();
        return;
      } catch (popupError) {
        console.log('Popup failed, trying redirect:', popupError);
      }

      // Last resort: redirect (will leave the current page)
      await instance.loginRedirect({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
      });
    } catch (error) {
      console.error('Re-authentication failed:', error);
      // Just close the modal and let the normal auth flow handle it
      onContinue();
      window.location.reload();
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Purple header accent */}
        <div className="h-2 bg-gradient-to-r from-sca-purple-500 to-sca-pink-500" />

        <div className="p-8 text-center">
          {/* Friendly illustration/icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-sca-purple-100 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-sca-purple-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>

          {/* Message */}
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Are you still there?
          </h2>
          <p className="text-gray-600 mb-8">
            Your session has timed out due to inactivity. Click below to continue where you left off.
          </p>

          {/* Continue button - styled like Microsoft login button */}
          <button
            onClick={handleContinue}
            disabled={isAuthenticating}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#2f2f2f] hover:bg-[#1a1a1a] disabled:bg-[#5f5f5f] text-white rounded-lg font-medium transition-colors"
          >
            {isAuthenticating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Reconnecting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Continue with Microsoft
              </>
            )}
          </button>

          {/* Optional: Sign out link */}
          <p className="mt-6 text-sm text-gray-500">
            Not you?{' '}
            <button
              onClick={() => {
                instance.logoutRedirect({
                  postLogoutRedirectUri: window.location.origin,
                });
              }}
              className="text-sca-purple-600 hover:text-sca-purple-700 font-medium"
            >
              Sign in with a different account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
